// /Users/stuartmcgeer/portalworx/frontend/src/layouts/layout.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import './layout.css';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../services/axiosInstance';
import { FaUser, FaSignOutAlt, FaChevronDown, FaChevronLeft, FaChevronRight, FaClipboardList } from 'react-icons/fa';
import { LuLayoutDashboard } from 'react-icons/lu';
import { IoMail, IoNotifications, IoSettings } from 'react-icons/io5'
import ViewButton from '../components/buttons/ViewButton';
import BreadCrumbs from '../components/common/BreadCrumbs'; // Import the BreadCrumbs component
import '../styles/global/GlobalStyles.css';

// Retrieves the base URL for media resources, replacing the local port for consistency
const getMediaBaseUrl = (): string => {
    return process.env.REACT_APP_MEDIA_BASE_URL || 'http://localhost:8000/media/';
};

// Defines the profile structure for user data
interface Profile {
    profile_picture: string;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    user_type: string;
}

// Layout component handles the overall layout structure including sidebar, topbar, and main content area
const Layout: React.FC = () => {
    const { username } = useAuth() as { username: string };
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [profilePictureUrl, setProfilePictureUrl] = useState<string>('/static/images/default_profile_picture.png');
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
        const savedState = localStorage.getItem('isSidebarOpen');
        return savedState !== null ? JSON.parse(savedState) : false;
    });
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState<number>(0); // New state for unread messages count
    const [unreadNotificationsCount, setUnreadNotificationsCount] = useState<number>(0); // New state for unread notifications count

    const navigate = useNavigate();
    const location = useLocation();

    const dropdownRef = useRef<HTMLLIElement>(null);
    const layoutRef = useRef<HTMLDivElement>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);

    // Fetches the user profile data from the server
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axiosInstance.get('user/', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                setProfile(response.data);

                if (response.data.profile_picture) {
                    const profilePicUrl = `${getMediaBaseUrl()}${response.data.profile_picture}`;
                    setProfilePictureUrl(profilePicUrl);
                } else {
                    setProfilePictureUrl(`${getMediaBaseUrl()}static/images/default_profile_picture.png`);
                }
                
            } catch (error) {
                console.error('Error fetching profile:', error);
            }
        };

        fetchProfile();
    }, []);

    // Fetch unread messages count
    useEffect(() => {
        const fetchUnreadMessagesCount = async () => {
            try {
                const response = await axiosInstance.get('/messages/unread-counts/'); // Endpoint to get unread message count
                const totalUnread = Object.values(response.data).reduce((sum: number, count: unknown) => sum + (count as number), 0); // Sum of all unread messages
                setUnreadMessagesCount(totalUnread);
            } catch (error) {
                console.error('Error fetching unread messages count:', error);
            }
        };
    
        fetchUnreadMessagesCount();
    }, []);

    // Fetch unread notifications count
    useEffect(() => {
        const fetchUnreadNotificationsCount = async () => {
            try {
                const response = await axiosInstance.get('/notifications/');
                const unreadCount = response.data.filter((notif: any) => !notif.is_read).length;
                setUnreadNotificationsCount(unreadCount);
            } catch (error) {
                console.error("Error fetching unread notifications count", error);
            }
        };
        fetchUnreadNotificationsCount();
    }, []);

    // Monitors window resize events to determine if the layout should switch to mobile mode
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Automatically closes the sidebar when navigating on mobile devices
    useEffect(() => {
        if (isMobile) {
            setIsSidebarOpen(false); // Auto-close sidebar only when navigating on mobile
        }
    }, [location.pathname, isMobile]);

    // Detects clicks outside certain elements to close dropdowns and sidebars on mobile
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }

            if (isMobile && isSidebarOpen && sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
                setIsSidebarOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobile, isSidebarOpen]);

    // Hides the dropdown menu whenever the user navigates to a new page
    useEffect(() => {
        setShowDropdown(false);
    }, [location.pathname]);

    // Toggles the visibility of the dropdown menu
    const toggleDropdown = () => {
        setShowDropdown(!showDropdown);
    };

    // Toggles the sidebar between open and collapsed states, saving the state in localStorage
    const toggleSidebar = () => {
        const newSidebarState = !isSidebarOpen;
        console.log('Sidebar toggled, new state:', newSidebarState);
        setIsSidebarOpen(newSidebarState);
        localStorage.setItem('isSidebarOpen', JSON.stringify(newSidebarState));
    };

    // Resets to a default profile picture if the image fails to load
    const handleImageError = () => {
        setProfilePictureUrl(`${getMediaBaseUrl()}static/images/default_profile_picture.png`);
    };
    

    // Navigates to the profile page when the profile button is clicked
    const handleViewProfileClick = () => {
        setShowDropdown(false);
        navigate('/profile');
    };

    // Determines the CSS class for user type based on the profile's user_type
    const getUserTypeClass = (userType: string) => {
        return `userType${userType.charAt(0).toUpperCase() + userType.slice(1)}`;
    };    

    return (
        <div
            ref={layoutRef}
            className={`layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-collapsed'} ${isMobile ? 'mobile' : ''}`}
        >
            {/* Sidebar Navigation */}
            <nav ref={sidebarRef} className="sidebar">
                <div className="sidebar-logo">
                    <Link to="/dashboard">
                        <img
                            src={
                                isMobile && isSidebarOpen
                                    ? `${getMediaBaseUrl()}static/logos/portal_worx_logo_wide_mobile.png`
                                    : isSidebarOpen
                                    ? `${getMediaBaseUrl()}static/logos/portal_worx_logo_wide.png`
                                    : `${getMediaBaseUrl()}static/logos/portal_worx_logo_symbol.png`
                            }
                            alt="Portal Worx Logo"
                            className="sidebar-logo-image"
                        />
                    </Link>
                </div>
                <div className="sidebar-link-container">
                    <ul className="sidebar-links">
                        {/* Dashboard Link */}
                        <li>
                            <Link to="/dashboard">
                                <LuLayoutDashboard /> {/* Dashboard Icon */}
                                <span className={`sidebar-text ${isSidebarOpen ? '' : 'hidden'}`}>Dashboard</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/admin">
                                <FaUser />
                                <span className={`sidebar-text ${isSidebarOpen ? '' : 'hidden'}`}>Admin</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/projects">
                                <FaClipboardList />
                                <span className={`sidebar-text ${isSidebarOpen ? '' : 'hidden'}`}>Projects</span>
                            </Link>
                        </li>
                    </ul>
                </div>
                <ul className="sidebar-links sidebar-bottom">
                    <li>
                        <Link to="/settings">
                            <IoSettings />
                            <span className={`sidebar-text ${isSidebarOpen ? "" : "hidden"}`}>Settings</span>
                        </Link>
                    </li>
                    <li>
                        <Link to="/logout">
                            <FaSignOutAlt />
                            <span className={`sidebar-text ${isSidebarOpen ? '' : 'hidden'}`}>Logout</span>
                        </Link>
                    </li>
                </ul>

                <button className="sidebar-toggle-circle" onClick={toggleSidebar}>
                    {isSidebarOpen ? <FaChevronLeft /> : <FaChevronRight />}
                </button>
            </nav>

            {/* Topbar */}
            <nav className="topbar">
                <div className="breadcrumbs">
                    <BreadCrumbs type="page" />
                </div>
                
                {/* personalLinks container with the direct message and notification icons */}
                <div className="personalLinks">
                    <Link to="/direct-message" className="topbar-mail-link">
                        <IoMail className="topbar-mail-icon" />
                        {unreadMessagesCount > 0 && (
                            <span className="notificationBadge">{unreadMessagesCount}</span>
                        )}
                    </Link>

                    {/* Notifications icon with badge */}
                    <Link to="/notifications" className="topbar-notifications-link">
                        <IoNotifications className="topbar-notifications-icon" />
                        {unreadNotificationsCount > 0 && (
                            <span className="notificationBadge">{unreadNotificationsCount}</span>
                        )}
                    </Link>
                </div>
                
                <ul className="topbar-links">
                    <li className="topbar-icon">
                        <i className="fa fa-bell"></i>
                    </li>
                    <li className="profile-dropdown" ref={dropdownRef}>
                        <div className="profile-link" onClick={toggleDropdown}>
                            <img
                                src={profilePictureUrl}
                                alt="Profile"
                                className="topbar-profile-picture"
                                onError={handleImageError}
                            />
                            {username && <span>{username}</span>}
                            <FaChevronDown className="dropdown-arrow" />
                        </div>
                        {showDropdown && (
                            <div className="dropdown-menu">
                                {profile && (
                                    <>
                                        <p className="dropdown-username">{profile.username}</p>
                                        <p><strong>First Name:</strong> {profile.first_name}</p>
                                        <p><strong>Last Name:</strong> {profile.last_name}</p>
                                        <p><strong>Email:</strong> {profile.email}</p>
                                        <p><strong>User Type: </strong>
                                            <span className={`userTypeLabel ${getUserTypeClass(profile.user_type)}`}>
                                                {profile.user_type}
                                            </span>
                                        </p>

                                        <ViewButton
                                            onClick={handleViewProfileClick}
                                            label="View Full Profile"
                                            className="view-profile-button"
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </li>
                </ul>
            </nav>

            {/* Main Content */}
            <main className="main-content">
                <Outlet />
            </main>
        </div>
    );
};

export default Layout;
